/**
 * SCRUM-370/371/372/373/374 — flujo principal de etiquetas de góndola:
 * elegir lista de precios → filtrar/seleccionar productos → elegir template
 * → configurar hoja → generar (crea el print_job y navega a la vista de
 * impresión, que resuelve los precios frescos al momento de imprimir).
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Panel } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { ProductPickerPanel } from "@/components/shelf-labels/ProductPickerPanel";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantProductFlags } from "@/hooks/use-tenant-product-flags";
import { useTenantContext } from "@/lib/tenant-context";
import { isFeatureApplicableToNiche } from "@/lib/feature-niche-matrix";
import { useProductPicker } from "@/hooks/use-product-picker";
import { toast } from "sonner";
import { LABEL_TEMPLATE_LIST, LABEL_TEMPLATES, type LabelFieldToggles, type LabelTemplateKey } from "@/lib/shelf-labels/templates";
import { defaultSheetConfig, type SheetConfig, type PaperSize, type Orientation } from "@/lib/shelf-labels/sheet-layout";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";

export const Route = createFileRoute("/app/catalogacion/etiquetas")({
  component: ShelfLabelsPage,
  head: () => ({ meta: [{ title: "Etiquetas y cartelería — Structura Market" }] }),
});

function ShelfLabelsPage() {
  const tid = useWorkspaceTenantId();
  const navigate = useNavigate();
  const productFlags = useTenantProductFlags();
  const { businessProfile } = useTenantContext();
  // SCRUM-451: HIDDEN_BY_NICHE no debe mostrarse como disponible solo porque
  // el plan lo habilita — mismo criterio que ya valida el backend
  // (assertSmFeatureApplicableToNiche en api.shelf-labels.*).
  const enabled = productFlags?.shelf_labels === true && isFeatureApplicableToNiche("shelf_labels", businessProfile);
  const priceListsEnabled = productFlags?.price_lists === true;

  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // ── Listas de precios ──
  const [priceLists, setPriceLists] = useState<Array<{ id: string; name: string }>>([]);
  const [priceListId, setPriceListId] = useState<string | null>(null);

  // ── Filtro + paginación + selección de productos ──
  const picker = useProductPicker(tid);
  const { mode, manualIds, categoriaId, brandId, q, selectedCount } = picker;

  // ── Template + campos ──
  const [templateKey, setTemplateKey] = useState<LabelTemplateKey>("gondola_clasica");
  const template = LABEL_TEMPLATES[templateKey];
  const [fields, setFields] = useState<LabelFieldToggles>(template.defaultFields);

  // ── Layout de hoja ──
  const [sheet, setSheet] = useState<SheetConfig>(() => defaultSheetConfig(template.suggestedWidthMm, template.suggestedHeightMm));

  // % de descuento del template Oferta — lo carga el usuario, no hay historial de "precio anterior" real.
  const [discountPct, setDiscountPct] = useState(15);

  // Al cambiar de template, resetear campos y sugerir un layout acorde (el usuario puede reajustar).
  useEffect(() => {
    setFields(template.defaultFields);
    setSheet(defaultSheetConfig(template.suggestedWidthMm, template.suggestedHeightMm));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey]);

  // ── Carga inicial: listas de precios ──
  useEffect(() => {
    if (!tid || !enabled) return;
    setLoading(true);
    apiGetJson<{ enabled?: boolean; lists?: Array<{ id: string; name: string }>; _lock?: PlanApiErrorPayload }>("/api/products/price-lists", tid)
      .then((plRes) => {
        if (plRes._lock) {
          setLock(plRes._lock);
          return;
        }
        const lists = plRes.enabled ? (plRes.lists ?? []) : [];
        setPriceLists(lists);
        if (lists.length === 1) setPriceListId(lists[0].id);
      })
      .catch(() => toast.error("No se pudo cargar la configuración"))
      .finally(() => setLoading(false));
  }, [tid, enabled]);

  const canGenerate = selectedCount > 0 && (priceLists.length <= 1 || !!priceListId);

  const generate = async () => {
    if (!tid || !canGenerate) return;
    setGenerating(true);
    try {
      const filter =
        mode === "manual"
          ? { mode: "manual" as const, productIds: [...manualIds] }
          : { mode: "all_filtered" as const, categoriaId: categoriaId || null, brandId: brandId || null, q: q || null };

      const job = await apiSendJson<{ job?: { id: string } }>(
        "/api/shelf-labels/print-jobs",
        "POST",
        {
          kind: "labels",
          priceListId: priceListId || undefined,
          filter,
          productCount: selectedCount,
          templateKey,
          sheetConfig: sheet,
          visualParams: { fields, discountPct: templateKey === "oferta" ? discountPct : undefined },
        },
        tid,
      );
      if (!job.job?.id) throw new Error();
      navigate({ to: "/print/etiquetas/$jobId", params: { jobId: job.job.id }, search: { tid } });
    } catch {
      toast.error("No se pudo generar el trabajo de impresión");
    } finally {
      setGenerating(false);
    }
  };

  if (!enabled) {
    // SCRUM-451: HIDDEN_BY_NICHE no debe ofrecer upgrade de plan — ningún
    // plan resuelve que el negocio no tenga catálogo de productos físicos.
    const hiddenByNiche = !isFeatureApplicableToNiche("shelf_labels", businessProfile);
    return (
      <FeatureLockedState
        payload={
          hiddenByNiche
            ? {
                code: "hidden_by_niche",
                feature: "shelf_labels",
                message: "Etiquetas de góndola requiere catálogo de productos físicos — no aplica a este tipo de negocio.",
              }
            : {
                code: "plan_feature_locked",
                feature: "shelf_labels",
                message: "Etiquetas de góndola y cartelería promocional está disponible en el plan Pro.",
              }
        }
      />
    );
  }
  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div>
      <CrudShellHeader
        title="Etiquetas de góndola"
        description="Imprimí etiquetas de precio para el local"
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/app/catalogacion/historial-impresiones">Ver historial</Link>
          </Button>
        }
      />
      <ClinicalPageShell offset="none" className="space-y-4 pt-6">
        {priceListsEnabled && priceLists.length > 1 ? (
          <Panel title="Lista de precios" description="El precio impreso sale de la lista elegida">
            <Select value={priceListId ?? ""} onValueChange={(v) => setPriceListId(v || null)}>
              <SelectTrigger className="max-w-sm" data-testid="shelf-labels-price-list">
                <SelectValue placeholder="Elegí una lista de precios" />
              </SelectTrigger>
              <SelectContent>
                {priceLists.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Panel>
        ) : null}

        <ProductPickerPanel picker={picker} />

        <Panel title="Template" description="Elegí un diseño de etiqueta">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {LABEL_TEMPLATE_LIST.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTemplateKey(t.key)}
                data-testid={`shelf-labels-template-${t.key}`}
                className={`rounded-xl border p-4 text-left transition-colors ${
                  templateKey === t.key ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"
                }`}
              >
                <p className="text-sm font-semibold">{t.name}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t.description}</p>
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(Object.keys(fields) as Array<keyof LabelFieldToggles>).map((key) => {
              const locked = template.lockedFields.includes(key);
              return (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={fields[key]}
                    disabled={locked}
                    onCheckedChange={(v) => setFields((f) => ({ ...f, [key]: v === true }))}
                  />
                  <span className={locked ? "text-muted-foreground" : ""}>{FIELD_LABELS[key]}</span>
                </label>
              );
            })}
          </div>

          {templateKey === "oferta" ? (
            <div className="mt-4 max-w-xs space-y-1.5">
              <label className="text-xs text-muted-foreground">% de descuento a mostrar</label>
              <Input
                type="number"
                min={1}
                max={99}
                value={discountPct}
                onChange={(e) => setDiscountPct(Math.min(99, Math.max(1, Number(e.target.value) || 1)))}
                data-testid="shelf-labels-discount-pct"
              />
              <p className="text-xs text-muted-foreground">
                Se usa para calcular el precio anterior tachado — no hay un historial de precios, así que el valor lo definís vos.
              </p>
            </div>
          ) : null}
        </Panel>

        <Panel title="Hoja de impresión" description="Pensado para imprimir y cortar en impresora común">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Tamaño</label>
              <Select value={sheet.paperSize} onValueChange={(v) => setSheet((s) => ({ ...s, paperSize: v as PaperSize }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A4">A4</SelectItem>
                  <SelectItem value="carta">Carta</SelectItem>
                  <SelectItem value="oficio">Oficio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Orientación</label>
              <Select value={sheet.orientation} onValueChange={(v) => setSheet((s) => ({ ...s, orientation: v as Orientation }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vertical">Vertical</SelectItem>
                  <SelectItem value="horizontal">Horizontal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Columnas</label>
              <Input type="number" min={1} max={10} value={sheet.columns} onChange={(e) => setSheet((s) => ({ ...s, columns: Math.max(1, Number(e.target.value) || 1) }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Filas</label>
              <Input type="number" min={1} max={20} value={sheet.rows} onChange={(e) => setSheet((s) => ({ ...s, rows: Math.max(1, Number(e.target.value) || 1) }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Margen (mm)</label>
              <Input type="number" min={0} max={40} value={sheet.marginTopMm} onChange={(e) => {
                const v = Math.max(0, Number(e.target.value) || 0);
                setSheet((s) => ({ ...s, marginTopMm: v, marginRightMm: v, marginBottomMm: v, marginLeftMm: v }));
              }} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Separación H (mm)</label>
              <Input type="number" min={0} max={20} value={sheet.gapHorizontalMm} onChange={(e) => setSheet((s) => ({ ...s, gapHorizontalMm: Math.max(0, Number(e.target.value) || 0) }))} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground">Separación V (mm)</label>
              <Input type="number" min={0} max={20} value={sheet.gapVerticalMm} onChange={(e) => setSheet((s) => ({ ...s, gapVerticalMm: Math.max(0, Number(e.target.value) || 0) }))} />
            </div>
          </div>
        </Panel>

        <div className="flex justify-end gap-2">
          <Button
            size="lg"
            disabled={!canGenerate || generating || loading}
            onClick={() => void generate()}
            data-testid="shelf-labels-generate"
          >
            {generating ? "Generando…" : `Generar preview (${selectedCount})`}
          </Button>
        </div>
      </ClinicalPageShell>
    </div>
  );
}

const FIELD_LABELS: Record<keyof LabelFieldToggles, string> = {
  nombre: "Nombre",
  precio: "Precio",
  unidadVenta: "Unidad de venta",
  sku: "SKU / código",
  barcode: "Código de barras",
  precioAnterior: "Precio anterior",
  descuento: "Descuento",
  precioPorUnidad: "Precio por kg/unidad",
  marca: "Marca",
  categoria: "Categoría",
  vigencia: "Vigencia / leyenda",
  logo: "Logo",
};
